import React, { useContext, useState } from "react";
import AppContext from "../../context/AppContext";
import { cn } from "../../lib/utils";

const CommonButton = ({
  children,
  onClick,
  disabled = false,
  className = "",
  variant = "primary",
  type,
  theme,
  tooltipText,
  tooltipPosition = "top",
  size: _size,
  ...rest
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { langMode } = useContext(AppContext);
  const getAdjustedPosition = (pos) => {
    if (langMode === "ar") {
      if (pos === "left") return "right";
      if (pos === "right") return "left";
    }
    return pos;
  };

  const buttonStyles = {
    primary:
      "bg-[#236DB4] text-white focus:ring-blue-300 dark:bg-[#236DB4] dark:focus:ring-blue-800",
    success:
      "bg-[#45B369] text-white focus:ring-green-300 dark:bg-[#45B369] dark:focus:ring-green-700",
    danger:
      "bg-[#FF0000] text-white focus:ring-red-300 dark:bg-[#FF0000] dark:focus:ring-red-700",
    warning:
      "bg-[#FF9F29] text-white focus:ring-amber-400 dark:bg-[#FF9F29]",
    gray: "bg-gray-500 text-white focus:ring-gray-300",
    blue: "bg-[#236DB4] text-white focus:ring-blue-400 dark:bg-[#236DB4]",
    red: "bg-[#FF0000] text-white focus:ring-red-300 dark:bg-[#FF0000] dark:focus:ring-red-800",
    outline:
      "bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 focus:ring-gray-300 dark:bg-white dark:text-gray-800",
  };

  const themeStyles =
    theme === "dark"
      ? "dark-mode"
      : theme === "high-contrast"
        ? " border bg-black text-white"
        : "";

  const tooltipPositionClasses = {
    top: "bottom-full border left-1/2 -translate-x-1/2 mb-3",
    bottom: "top-full border left-1/2 -translate-x-1/2 mt-3",
    left: "right-full border top-1/2 -translate-y-1/2 mr-3",
    right: "left-full border top-1/2 -translate-y-1/2 ml-3",
  };

  const tooltipArrowClasses = {
    top: `
      before:content-[''] before:absolute border before:left-1/2 before:bottom-[-4px] 
      before:-translate-x-1/2 before:w-2 border before:h-2 before:bg-white
      before:rotate-45 before:rounded-sm border before:shadow-xl before:z-10 
    `,
    bottom: `
      before:content-[''] before:absolute border before:left-1/2 before:top-[-4px] 
      before:-translate-x-1/2 before:w-2 border before:h-2 before:bg-white
      before:rotate-45 before:rounded-sm  border before:shadow-xl before:z-10
    `,
    left: `
      before:content-[''] before:absolute border before:right-[-4px] before:top-1/2 
      before:-translate-y-1/2 before:w-2 border before:h-2 before:bg-white
      before:rotate-45 before:rounded-sm border before:shadow-xl before:z-10
    `,
    right: `
      before:content-[''] before:absolute border before:left-[-4px] before:top-1/2 
      before:-translate-y-1/2 before:w-2 border before:h-2 before:bg-white
      before:rotate-45 before:rounded-sm border before:shadow-xl before:z-10
    `,
  };

  const finalTooltipPosition = getAdjustedPosition(tooltipPosition);

  const buttonClassName = cn(
    "inline-flex items-center justify-center gap-2.5 text-sm font-medium rounded-lg focus:ring-4 focus:outline-none transition-all box-border",
    buttonStyles[variant] ?? buttonStyles.primary,
    themeStyles,
    disabled && "opacity-60 cursor-not-allowed",
    className,
    "!min-h-[36px] !h-9 !max-h-9 !px-4 !py-0"
  );

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => tooltipText && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type={type}
        disabled={disabled}
        className={buttonClassName}
        onClick={onClick}
        {...rest}
      >
        {children}
      </button>

      {isHovered && tooltipText && (
        <div
          className={`
      absolute z-[9999] p-2 text-sm text-black bg-white border rounded-lg 
      shadow-xl whitespace-nowrap transition-opacity duration-300 
      ${tooltipPositionClasses[finalTooltipPosition]}
      ${tooltipArrowClasses[finalTooltipPosition]}
    `}
        >
          {tooltipText}
        </div>
      )}
    </div>
  );
};

export default CommonButton;
